import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { CheckboxField, SelectField, SwitchField, TextField } from '@/components/form-fields';
import {
  AddButton,
  EmptyState,
  ListRow,
  ListShell,
  ReadOnlyNotice,
  SectionCard,
  SettingsSkeleton,
} from '@/components/settings/settings-kit';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/lib/auth';
import {
  addPickupSlot,
  CHANNEL_TYPES,
  type ChannelRecord,
  type ChannelType,
  copyPickupSlotsToWeek,
  deleteChannel,
  deleteChannelMenu,
  deleteFee,
  deleteLimit,
  deletePickupSlot,
  FEE_KINDS,
  type FeeKind,
  type FeeRecord,
  FULFILLMENT_TYPES,
  type FulfillmentType,
  LIMIT_UNITS,
  type LimitRecord,
  listChannelMenus,
  listChannels,
  listFees,
  listLimits,
  listPickupSlots,
  REGULATORY_CATEGORIES,
  saveChannel,
  saveChannelMenu,
  saveFee,
  saveLimit,
} from '@/lib/settings/commerce';
import { DAY_NAMES, listLocations } from '@/lib/settings/locations';
import { useForm } from '@/lib/use-form';

/**
 * Fees, purchase limits, pickup times and sales channels.
 *
 * Every one of these is already read by something -- `resolveOrderFees`,
 * `check_purchase_limits`, checkout's slot picker, the menu -- and none of
 * them had a screen, so they were whatever onboarding wrote and nothing else.
 */

const money = z
  .string()
  .trim()
  .regex(/^\d+(\.\d{1,2})?$/, 'Amounts look like 3.50');

/* -------------------------------------------------------------------------- */
/* Fees                                                                        */
/* -------------------------------------------------------------------------- */

const feeSchema = z
  .object({
    name: z.string().trim().min(1, 'Name the fee').max(80),
    kind: z.enum(['delivery', 'service', 'payment_surcharge', 'other']),
    basis: z.enum(['amount', 'rate']),
    amount: money.or(z.literal('')),
    rate: z
      .string()
      .trim()
      .regex(/^\d{1,2}(\.\d{1,2})?$/, 'A percentage looks like 2.5')
      .or(z.literal('')),
    taxable: z.boolean(),
    appliesToAll: z.boolean(),
    fulfillmentTypes: z.array(z.string()),
    active: z.boolean(),
  })
  .refine((values) => (values.basis === 'amount' ? values.amount !== '' : values.rate !== ''), {
    message: 'Enter the amount or the rate',
    path: ['amount'],
  });

type FeeValues = z.infer<typeof feeSchema>;

function toFeeValues(fee: FeeRecord | null): FeeValues {
  return {
    name: fee?.name ?? '',
    kind: fee?.kind ?? 'service',
    basis: fee?.rateBps != null ? 'rate' : 'amount',
    amount: fee?.amountCents != null ? (fee.amountCents / 100).toFixed(2) : '',
    rate: fee?.rateBps != null ? (fee.rateBps / 100).toString() : '',
    taxable: fee?.taxable ?? false,
    appliesToAll: !fee?.fulfillmentTypes,
    fulfillmentTypes: fee?.fulfillmentTypes ?? [],
    active: fee?.active ?? true,
  };
}

export function FeeSettings({ accountId }: { accountId: string }) {
  const { can } = useAuth();
  const canManage = can('settings.manage');
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<FeeRecord | 'new' | null>(null);

  const fees = useQuery({ queryKey: ['fees', accountId], queryFn: () => listFees(accountId) });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['fees', accountId] });

  const remove = useMutation({
    mutationFn: (id: string) => deleteFee(accountId, id),
    onSuccess: () => {
      toast.success('Fee removed.');
      refresh();
    },
    onError: (error: Error) =>
      toast.error(
        error.message.includes('violates foreign key')
          ? 'Orders have been charged this fee, so it cannot be deleted. Switch it off instead.'
          : error.message,
      ),
  });

  if (fees.isLoading) return <SettingsSkeleton />;

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      {!canManage && <ReadOnlyNotice permission="settings.manage" />}

      <SectionCard
        title="Fees"
        description="Charged on every order that matches, on top of the goods. Delivery zones carry their own fee; these are the rest."
        action={
          <AddButton onClick={() => setEditing('new')} disabled={!canManage}>
            Add fee
          </AddButton>
        }
      >
        {fees.data?.length ? (
          <ListShell>
            {fees.data.map((fee) => (
              <ListRow
                key={fee.id}
                title={fee.name}
                subtitle={`${
                  fee.amountCents != null
                    ? `$${(fee.amountCents / 100).toFixed(2)}`
                    : `${(fee.rateBps ?? 0) / 100}% of the subtotal`
                } · ${fee.taxable ? 'taxable' : 'not taxed'} · ${
                  fee.fulfillmentTypes?.length
                    ? fee.fulfillmentTypes.join(', ')
                    : 'every fulfillment type'
                }`}
                badges={
                  <>
                    <Badge variant="secondary">{fee.kind.replace(/_/g, ' ')}</Badge>
                    {!fee.active && <Badge variant="outline">Off</Badge>}
                  </>
                }
                onClick={canManage ? () => setEditing(fee) : undefined}
                actions={
                  canManage ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => remove.mutate(fee.id)}
                    >
                      Remove
                    </Button>
                  ) : null
                }
              />
            ))}
          </ListShell>
        ) : (
          <EmptyState>No fees, so orders are charged for goods, tax and delivery only.</EmptyState>
        )}

        {editing && (
          <FeeForm
            accountId={accountId}
            fee={editing === 'new' ? null : editing}
            onDone={() => {
              setEditing(null);
              refresh();
            }}
            onCancel={() => setEditing(null)}
          />
        )}
      </SectionCard>
    </div>
  );
}

function FeeForm({
  accountId,
  fee,
  onDone,
  onCancel,
}: {
  accountId: string;
  fee: FeeRecord | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const { values, errors, set, submit } = useForm(toFeeValues(fee), feeSchema);

  const save = useMutation({
    mutationFn: (parsed: FeeValues) =>
      saveFee(accountId, {
        id: fee?.id,
        name: parsed.name,
        kind: parsed.kind as FeeKind,
        amountCents: parsed.basis === 'amount' ? Math.round(Number(parsed.amount) * 100) : null,
        rateBps: parsed.basis === 'rate' ? Math.round(Number(parsed.rate) * 100) : null,
        taxable: parsed.taxable,
        fulfillmentTypes: parsed.appliesToAll
          ? null
          : (parsed.fulfillmentTypes as FulfillmentType[]),
        active: parsed.active,
      }),
    onSuccess: () => {
      toast.success(fee ? 'Fee saved.' : 'Fee added.');
      onDone();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <form
      onSubmit={submit((parsed) => save.mutate(parsed))}
      noValidate
      className="flex flex-col gap-4 rounded-lg border border-dashed p-4"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="fee-name"
          label="Name"
          description="Shown on the receipt exactly as written."
          placeholder="Service fee"
          value={values.name}
          error={errors.name}
          onChange={(value) => set('name', value)}
        />
        <SelectField
          id="fee-kind"
          label="Kind"
          value={values.kind}
          options={FEE_KINDS.map((kind) => ({ value: kind, label: kind.replace(/_/g, ' ') }))}
          onChange={(value) => set('kind', value as FeeValues['kind'])}
        />
        <SelectField
          id="fee-basis"
          label="Charged as"
          value={values.basis}
          options={[
            { value: 'amount', label: 'A fixed amount' },
            { value: 'rate', label: 'A percentage of the subtotal' },
          ]}
          onChange={(value) => set('basis', value as FeeValues['basis'])}
        />
        {values.basis === 'amount' ? (
          <TextField
            id="fee-amount"
            label="Amount"
            description="Dollars."
            value={values.amount}
            error={errors.amount}
            onChange={(value) => set('amount', value)}
          />
        ) : (
          <TextField
            id="fee-rate"
            label="Rate"
            description="Percent of the goods subtotal."
            value={values.rate}
            error={errors.rate}
            onChange={(value) => set('rate', value)}
          />
        )}
      </div>

      <SwitchField
        id="fee-all"
        label="Charge on every fulfillment type"
        checked={values.appliesToAll}
        onChange={(checked) => set('appliesToAll', checked)}
      />

      {!values.appliesToAll && (
        <div className="flex flex-wrap gap-4">
          {FULFILLMENT_TYPES.map((type) => (
            <CheckboxField
              key={type}
              id={`fee-type-${type}`}
              label={type.replace(/_/g, ' ')}
              checked={values.fulfillmentTypes.includes(type)}
              onChange={(checked) =>
                set('fulfillmentTypes', (current) =>
                  checked ? [...current, type] : current.filter((value) => value !== type),
                )
              }
            />
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-6">
        <SwitchField
          id="fee-taxable"
          label="Taxable"
          description="Sales tax applies to the fee itself."
          checked={values.taxable}
          onChange={(checked) => set('taxable', checked)}
        />
        <SwitchField
          id="fee-active"
          label="Active"
          checked={values.active}
          onChange={(checked) => set('active', checked)}
        />
      </div>

      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : fee ? 'Save fee' : 'Add fee'}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/* -------------------------------------------------------------------------- */
/* Purchase limits                                                             */
/* -------------------------------------------------------------------------- */

const limitSchema = z.object({
  jurisdiction: z.string().trim().min(2, 'Two-letter state').max(10),
  regulatoryCategory: z.enum(REGULATORY_CATEGORIES),
  unit: z.enum(LIMIT_UNITS),
  limitValue: z
    .string()
    .trim()
    .regex(/^\d+(\.\d{1,3})?$/, 'A number, e.g. 28.5'),
  windowHours: z
    .string()
    .trim()
    .regex(/^\d{1,4}$/, 'Hours, as a whole number'),
  customerType: z.enum(['any', 'recreational', 'medical', 'state_medical']),
  active: z.boolean(),
});

type LimitValues = z.infer<typeof limitSchema>;

export function LimitSettings({ accountId }: { accountId: string }) {
  const { can } = useAuth();
  const canManage = can('compliance.manage');
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<LimitRecord | 'new' | null>(null);

  const limits = useQuery({
    queryKey: ['limits', accountId],
    queryFn: () => listLimits(accountId),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['limits', accountId] });

  const remove = useMutation({
    mutationFn: (id: string) => deleteLimit(accountId, id),
    onSuccess: () => {
      toast.success('Limit removed.');
      refresh();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (limits.isLoading) return <SettingsSkeleton />;

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      {!canManage && <ReadOnlyNotice permission="compliance.manage" />}

      <SectionCard
        title="Purchase limits"
        description="Checked at the register and at checkout against everything the customer has already bought in the window. Removing a rule stops that check."
        action={
          <AddButton onClick={() => setEditing('new')} disabled={!canManage}>
            Add limit
          </AddButton>
        }
      >
        {limits.data?.length ? (
          <ListShell>
            {limits.data.map((limit) => (
              <ListRow
                key={limit.id}
                title={`${limit.regulatoryCategory.replace(/_/g, ' ')} · ${limit.limitValue} ${limit.unit.replace(/_/g, ' ')}`}
                subtitle={`Per ${limit.windowHours}h · ${limit.customerType ?? 'every customer type'} · ${limit.jurisdiction}`}
                badges={!limit.active ? <Badge variant="outline">Off</Badge> : null}
                onClick={canManage ? () => setEditing(limit) : undefined}
                actions={
                  canManage ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => remove.mutate(limit.id)}
                    >
                      Remove
                    </Button>
                  ) : null
                }
              />
            ))}
          </ListShell>
        ) : (
          <EmptyState>
            No limits, so nothing stops a customer buying an unlawful quantity in one day.
          </EmptyState>
        )}

        {editing && (
          <LimitForm
            accountId={accountId}
            limit={editing === 'new' ? null : editing}
            onDone={() => {
              setEditing(null);
              refresh();
            }}
            onCancel={() => setEditing(null)}
          />
        )}
      </SectionCard>
    </div>
  );
}

function LimitForm({
  accountId,
  limit,
  onDone,
  onCancel,
}: {
  accountId: string;
  limit: LimitRecord | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const initial: LimitValues = {
    jurisdiction: limit?.jurisdiction ?? 'CA',
    regulatoryCategory: limit?.regulatoryCategory ?? 'flower',
    unit: limit?.unit ?? 'grams',
    limitValue: String(limit?.limitValue ?? 28.5),
    windowHours: String(limit?.windowHours ?? 24),
    customerType: limit?.customerType ?? 'any',
    active: limit?.active ?? true,
  };

  const { values, errors, set, submit } = useForm(initial, limitSchema);

  const save = useMutation({
    mutationFn: (parsed: LimitValues) =>
      saveLimit(accountId, {
        id: limit?.id,
        jurisdiction: parsed.jurisdiction.toUpperCase(),
        regulatoryCategory: parsed.regulatoryCategory,
        unit: parsed.unit,
        limitValue: Number(parsed.limitValue),
        windowHours: Number(parsed.windowHours),
        customerType: parsed.customerType === 'any' ? null : parsed.customerType,
        active: parsed.active,
      }),
    onSuccess: () => {
      toast.success(limit ? 'Limit saved.' : 'Limit added.');
      onDone();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <form
      onSubmit={submit((parsed) => save.mutate(parsed))}
      noValidate
      className="flex flex-col gap-4 rounded-lg border border-dashed p-4"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="limit-category"
          label="Category"
          value={values.regulatoryCategory}
          options={REGULATORY_CATEGORIES.map((category) => ({
            value: category,
            label: category.replace(/_/g, ' '),
          }))}
          onChange={(value) =>
            set('regulatoryCategory', value as LimitValues['regulatoryCategory'])
          }
        />
        <SelectField
          id="limit-unit"
          label="Measured in"
          value={values.unit}
          options={LIMIT_UNITS.map((unit) => ({ value: unit, label: unit.replace(/_/g, ' ') }))}
          onChange={(value) => set('unit', value as LimitValues['unit'])}
        />
        <TextField
          id="limit-value"
          label="Limit"
          value={values.limitValue}
          error={errors.limitValue}
          onChange={(value) => set('limitValue', value)}
        />
        <TextField
          id="limit-window"
          label="Window (hours)"
          description="California counts a rolling 24 hours."
          value={values.windowHours}
          error={errors.windowHours}
          onChange={(value) => set('windowHours', value)}
        />
        <SelectField
          id="limit-customer"
          label="Applies to"
          value={values.customerType}
          options={[
            { value: 'any', label: 'Every customer type' },
            { value: 'recreational', label: 'Recreational' },
            { value: 'medical', label: 'Medical' },
            { value: 'state_medical', label: 'State medical' },
          ]}
          onChange={(value) => set('customerType', value as LimitValues['customerType'])}
        />
        <TextField
          id="limit-jurisdiction"
          label="State"
          value={values.jurisdiction}
          error={errors.jurisdiction}
          onChange={(value) => set('jurisdiction', value)}
        />
      </div>

      <SwitchField
        id="limit-active"
        label="Enforced"
        checked={values.active}
        onChange={(checked) => set('active', checked)}
      />

      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : limit ? 'Save limit' : 'Add limit'}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/* -------------------------------------------------------------------------- */
/* Pickup slots                                                                */
/* -------------------------------------------------------------------------- */

export function PickupSettings({ accountId }: { accountId: string }) {
  const { can } = useAuth();
  const canManage = can('locations.manage');
  const queryClient = useQueryClient();
  const [locationId, setLocationId] = useState<string>('');
  const [draft, setDraft] = useState({
    dayOfWeek: '1',
    fulfillmentType: 'pickup' as 'pickup' | 'curbside',
    startsAt: '10:00',
    endsAt: '12:00',
    capacity: '10',
  });

  const locations = useQuery({
    queryKey: ['locations', accountId],
    queryFn: () => listLocations(accountId),
  });

  const stores = (locations.data ?? []).filter((location) => location.allowsPickup);
  const selected = locationId || stores[0]?.id || '';

  const slots = useQuery({
    queryKey: ['pickup-slots', accountId, selected],
    queryFn: () => listPickupSlots(accountId, selected),
    enabled: Boolean(selected),
  });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['pickup-slots', accountId, selected] });

  const add = useMutation({
    mutationFn: () =>
      addPickupSlot(accountId, {
        locationId: selected,
        dayOfWeek: Number(draft.dayOfWeek),
        fulfillmentType: draft.fulfillmentType,
        startsAt: draft.startsAt,
        endsAt: draft.endsAt,
        capacity: draft.capacity.trim() === '' ? null : Number(draft.capacity),
      }),
    onSuccess: () => {
      toast.success('Pickup slot added.');
      refresh();
    },
    onError: (error: Error) =>
      toast.error(
        error.message.includes('exclusion') || error.message.includes('overlap')
          ? 'That slot overlaps another on the same day.'
          : error.message,
      ),
  });

  const copy = useMutation({
    mutationFn: () => {
      const source = (slots.data ?? []).filter(
        (slot) => slot.dayOfWeek === Number(draft.dayOfWeek),
      );

      if (!source.length) throw new Error('Add that day first, then copy it.');

      return copyPickupSlotsToWeek(accountId, selected, source);
    },
    onSuccess: () => {
      toast.success('Copied across the week.');
      refresh();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const remove = useMutation({
    mutationFn: (id: number) => deletePickupSlot(accountId, id),
    onSuccess: refresh,
    onError: (error: Error) => toast.error(error.message),
  });

  if (locations.isLoading) return <SettingsSkeleton />;

  if (!stores.length) {
    return (
      <EmptyState>
        No location offers pickup yet. Turn pickup on for a store under Locations first.
      </EmptyState>
    );
  }

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      {!canManage && <ReadOnlyNotice permission="locations.manage" />}

      <SectionCard
        title="Pickup times"
        description="The slots a shopper can choose at checkout. Without any, pickup orders arrive with no promised time."
      >
        <SelectField
          id="pickup-location"
          label="Store"
          value={selected}
          options={stores.map((store) => ({ value: store.id, label: store.name }))}
          onChange={setLocationId}
        />

        {slots.data?.length ? (
          <ListShell>
            {slots.data.map((slot) => (
              <ListRow
                key={slot.id}
                title={`${DAY_NAMES[slot.dayOfWeek]} · ${slot.startsAt}–${slot.endsAt}`}
                subtitle={`${slot.fulfillmentType} · ${
                  slot.capacity === null ? 'unlimited' : `${slot.capacity} orders`
                }`}
                actions={
                  canManage ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => remove.mutate(slot.id)}
                    >
                      Remove
                    </Button>
                  ) : null
                }
              />
            ))}
          </ListShell>
        ) : (
          <EmptyState>No pickup slots at this store.</EmptyState>
        )}

        {canManage && (
          <div className="flex flex-wrap items-end gap-3">
            <SelectField
              id="pickup-day"
              label="Day"
              className="w-36"
              value={draft.dayOfWeek}
              options={DAY_NAMES.map((name, index) => ({ value: String(index), label: name }))}
              onChange={(value) => setDraft((current) => ({ ...current, dayOfWeek: value }))}
            />
            <SelectField
              id="pickup-type"
              label="For"
              className="w-32"
              value={draft.fulfillmentType}
              options={[
                { value: 'pickup', label: 'Pickup' },
                { value: 'curbside', label: 'Curbside' },
              ]}
              onChange={(value) =>
                setDraft((current) => ({
                  ...current,
                  fulfillmentType: value as 'pickup' | 'curbside',
                }))
              }
            />
            <Field className="w-28">
              <FieldLabel htmlFor="pickup-start">From</FieldLabel>
              <Input
                id="pickup-start"
                type="time"
                value={draft.startsAt}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, startsAt: event.target.value }))
                }
              />
            </Field>
            <Field className="w-28">
              <FieldLabel htmlFor="pickup-end">To</FieldLabel>
              <Input
                id="pickup-end"
                type="time"
                value={draft.endsAt}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, endsAt: event.target.value }))
                }
              />
            </Field>
            <Field className="w-24">
              <FieldLabel htmlFor="pickup-capacity">Capacity</FieldLabel>
              <Input
                id="pickup-capacity"
                inputMode="numeric"
                value={draft.capacity}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, capacity: event.target.value }))
                }
              />
            </Field>

            <Button type="button" size="sm" onClick={() => add.mutate()} disabled={add.isPending}>
              Add
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => copy.mutate()}
              disabled={copy.isPending}
            >
              Copy this day to the week
            </Button>
          </div>
        )}
      </SectionCard>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Channels                                                                    */
/* -------------------------------------------------------------------------- */

const channelSchema = z.object({
  name: z.string().trim().min(1, 'Name the channel').max(80),
  slug: z
    .string()
    .trim()
    .min(1)
    .max(60)
    .regex(/^[a-z0-9-]+$/, 'Lower case letters, numbers and dashes'),
  type: z.enum(['web', 'pos', 'kiosk', 'marketplace']),
  active: z.boolean(),
});

type ChannelValues = z.infer<typeof channelSchema>;

export function ChannelSettings({ accountId }: { accountId: string }) {
  const { can } = useAuth();
  const canManage = can('settings.manage');
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<ChannelRecord | 'new' | null>(null);

  const channels = useQuery({
    queryKey: ['sales-channels', accountId],
    queryFn: () => listChannels(accountId),
  });

  const menus = useQuery({
    queryKey: ['channel-menus', accountId],
    queryFn: () => listChannelMenus(accountId),
  });

  const locations = useQuery({
    queryKey: ['locations', accountId],
    queryFn: () => listLocations(accountId),
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['sales-channels', accountId] });
    queryClient.invalidateQueries({ queryKey: ['channel-menus', accountId] });
  };

  const remove = useMutation({
    mutationFn: (id: string) => deleteChannel(accountId, id),
    onSuccess: () => {
      toast.success('Channel removed.');
      refresh();
    },
    onError: (error: Error) =>
      toast.error(
        error.message.includes('violates foreign key')
          ? 'Orders were taken on this channel, so it cannot be deleted. Switch it off instead.'
          : error.message,
      ),
  });

  const toggleMenu = useMutation({
    mutationFn: (input: {
      channelId: string;
      locationId: string;
      id?: string;
      published: boolean;
    }) =>
      saveChannelMenu(accountId, {
        id: input.id,
        channelId: input.channelId,
        locationId: input.locationId,
        published: input.published,
        showOutOfStock: false,
      }),
    onSuccess: () => {
      toast.success('Menu updated.');
      refresh();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const removeMenu = useMutation({
    mutationFn: (id: string) => deleteChannelMenu(accountId, id),
    onSuccess: refresh,
    onError: (error: Error) => toast.error(error.message),
  });

  if (channels.isLoading || locations.isLoading) return <SettingsSkeleton />;

  const stores = (locations.data ?? []).filter((location) => location.type !== 'vehicle');

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      {!canManage && <ReadOnlyNotice permission="settings.manage" />}

      <SectionCard
        title="Sales channels"
        description="Where orders come from. Every order records one, which is how the reports separate web from counter."
        action={
          <AddButton onClick={() => setEditing('new')} disabled={!canManage}>
            Add channel
          </AddButton>
        }
      >
        {channels.data?.length ? (
          <ListShell>
            {channels.data.map((channel) => (
              <ListRow
                key={channel.id}
                title={channel.name}
                subtitle={channel.slug}
                badges={
                  <>
                    <Badge variant="secondary">{channel.type}</Badge>
                    {!channel.active && <Badge variant="outline">Off</Badge>}
                  </>
                }
                onClick={canManage ? () => setEditing(channel) : undefined}
                actions={
                  canManage ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => remove.mutate(channel.id)}
                    >
                      Remove
                    </Button>
                  ) : null
                }
              />
            ))}
          </ListShell>
        ) : (
          <EmptyState>
            No channels. Orders will record none, and reports cannot split them.
          </EmptyState>
        )}

        {editing && (
          <ChannelForm
            accountId={accountId}
            channel={editing === 'new' ? null : editing}
            onDone={() => {
              setEditing(null);
              refresh();
            }}
            onCancel={() => setEditing(null)}
          />
        )}
      </SectionCard>

      <SectionCard
        title="Menus"
        description="Which store's catalog each channel shows. A channel with no published menu falls back to the whole catalog."
      >
        {channels.data?.length && stores.length ? (
          <ListShell>
            {channels.data.flatMap((channel) =>
              stores.map((store) => {
                const menu = menus.data?.find(
                  (row) => row.channelId === channel.id && row.locationId === store.id,
                );

                return (
                  <ListRow
                    key={`${channel.id}-${store.id}`}
                    title={`${channel.name} · ${store.name}`}
                    subtitle={
                      menu
                        ? `${menu.published ? 'Published' : 'Draft'}${
                            menu.excludedProducts
                              ? ` · ${menu.excludedProducts} products hidden`
                              : ''
                          }`
                        : 'No menu'
                    }
                    actions={
                      canManage ? (
                        <div className="flex items-center gap-1">
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              toggleMenu.mutate({
                                id: menu?.id,
                                channelId: channel.id,
                                locationId: store.id,
                                published: !menu?.published,
                              })
                            }
                          >
                            {menu?.published ? 'Unpublish' : 'Publish'}
                          </Button>
                          {menu && (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => removeMenu.mutate(menu.id)}
                            >
                              Remove
                            </Button>
                          )}
                        </div>
                      ) : null
                    }
                  />
                );
              }),
            )}
          </ListShell>
        ) : (
          <EmptyState>Add a channel and a location to pair them.</EmptyState>
        )}
      </SectionCard>
    </div>
  );
}

function ChannelForm({
  accountId,
  channel,
  onDone,
  onCancel,
}: {
  accountId: string;
  channel: ChannelRecord | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const { values, errors, set, submit } = useForm<ChannelValues>(
    {
      name: channel?.name ?? '',
      slug: channel?.slug ?? '',
      type: channel?.type ?? 'web',
      active: channel?.active ?? true,
    },
    channelSchema,
  );

  const save = useMutation({
    mutationFn: (parsed: ChannelValues) =>
      saveChannel(accountId, {
        id: channel?.id,
        name: parsed.name,
        slug: parsed.slug,
        type: parsed.type as ChannelType,
        active: parsed.active,
      }),
    onSuccess: () => {
      toast.success(channel ? 'Channel saved.' : 'Channel added.');
      onDone();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <form
      onSubmit={submit((parsed) => save.mutate(parsed))}
      noValidate
      className="flex flex-col gap-4 rounded-lg border border-dashed p-4"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="channel-name"
          label="Name"
          value={values.name}
          error={errors.name}
          onChange={(value) => {
            set('name', value);

            if (!channel && !values.slug) {
              set(
                'slug',
                value
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, '-')
                  .replace(/^-|-$/g, ''),
              );
            }
          }}
        />
        <TextField
          id="channel-slug"
          label="Short code"
          value={values.slug}
          error={errors.slug}
          onChange={(value) => set('slug', value)}
        />
        <SelectField
          id="channel-type"
          label="Type"
          value={values.type}
          options={CHANNEL_TYPES.map((type) => ({ value: type, label: type }))}
          onChange={(value) => set('type', value as ChannelValues['type'])}
        />
      </div>

      <SwitchField
        id="channel-active"
        label="Active"
        checked={values.active}
        onChange={(checked) => set('active', checked)}
      />

      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : channel ? 'Save channel' : 'Add channel'}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
